import Vue from 'vue'
import { i18n } from '../../helper/i18n/index'
import SpSelect from '../Forms/SpSelect.vue'
import parseDependency from '../../utils/parseDependency'
import parseRules from '../../utils/parseRules'
import parsePlaceholder from '../../utils/parsePlaceholder'
import SpTableSelect from '../Forms/SpTableSelect'

const defaultProps = {
  children: 'children',
  label: 'label'
}

function getProps(field, model, inTable, state, viewName) {
  /* TODO: finish this */
  switch (field.type) {
    case 'tree': {
      let data = typeof field.items === 'function' ? field.items(state) : field.items
      if (field.subType === 'check-tree') {
        data = [{
          id: '@ALL',
          label: i18n('Select / Unselect All'),
          children: data
        }]
      }
      return {
        data,
        multiple: field.multiple,
        expandOnClickNode: field.subType !== 'check-tree',
        showCheckbox: true,
        defaultExpandAll: true,
        nodeKey: 'id'
      }
    }
    default:
      // 支持计算属性
      const props = { ...field.options.props }
      for (let key in props) {
        if (typeof props[key] === 'function') {
          props[key] = props[key].call(model, viewName)
        }
      }
      return {
        items: field.items,
        multiple: field.multiple,
        value: model[field.key],
        type: field.subType,
        highlightCurrent: true,
        size: inTable ? 'medium' : 'mini',
        ...(props || defaultProps),
        ...parsePlaceholder(field.placeholder),
        beforeCreateOptions: field.options.beforeCreateOptions,
        getTableList: field.options.getTableList,
        filterSelectable: field.options.filterSelectable
      }
  }
}

export function generateFormItem(h, field, model, state = {}, on = {}, inTable = false, affected = {}, schemaHash = {}, viewName) {
  let component = ''
  // special judge for sp-select
  // TODO: other HOCs
  if (field.type === 'select') {
    component = `sp-${field.type}`
  } else if (field.type === 'tableSelect') {
    component = SpTableSelect
  } else {
    component = `el-${field.type}`
  }
  return (
    <el-form-item prop={field.key} label={inTable ? i18n(field.label) : null}>
      {
        h(component, {
          ref: field.key,
          class: field.subType ? `sp-${field.subType}` : '',
          props: getProps(field, model, inTable, state, viewName),
          on: {
            ...on,
            input: val => {
              // change model
              model[field.key] = val
              // original on
              if (typeof on.input === 'function') {
                on.input(val)
              }
              // dependency
              if (affected[field.key]) {
                for (const d of affected[field.key]) {
                  const deps = [...schemaHash[d].options.deps]
                  const handler = deps.pop()
                  const values = deps.map(key => model[key])
                  model[d] = handler.apply(schemaHash[d], values)
                }
              }
            },
            selectionChange: val => on.selectionChange && on.selectionChange(val),
            check: (node, { checkedKeys }) => on.check && on.check(node, checkedKeys)
          }
        })
      }
    </el-form-item>
  )
}

export function generateForm(h, options, model, rules, children = [], slots = []) {
  return h('el-form', {
    ref: 'form',
    props: Object.assign({}, options || {}, {
      model,
      rules,
      inline: options ? Boolean(options.inline) : false,
      disabled: options ? Boolean(options.disabled) : false
    })
  }, [...children, ...slots])
}

export default {
  name: 'FormTpl',
  components: { SpSelect },
  render(h) {
    const nodes = this.fields.map(field => generateFormItem(
      h,
      field,
      this.model,
      this.$store.state,
      this.parseEventListeners(field),
      true,
      this.dependency,
      this.schemaHash,
      this.viewName
    ))
    let children = []
    if (!this.options.noValidate && this.fields.some(field => field.options.group)) {
      // TODO: group all schemas
      const groups = {}
      const defaultGroup = []
      this.fields.forEach((field, index) => {
        const node = nodes[index]
        const group = field.options.group
        if (group) {
          if (groups[group]) {
            groups[group].push(node)
          } else {
            groups[group] = [node]
          }
        } else {
          defaultGroup.push(node)
        }
      })
      groups['Other'] = defaultGroup
      children = Object.entries(groups).map(([label, nodes]) => (
        <fieldset>
          <legend>{this.$i18n(label)}</legend>
          <div class="sp-form-table">{nodes}</div>
        </fieldset>
      ))
    } else {
      children = nodes
    }
    return generateForm(h, this.options, this.model, this.parsedRules, children, this.$slots.default)
  },
  methods: {
    parseEventListeners({ type, key, on, options}) {
      this.parsedOn[key] = this.parsedOn[key] || {}
      let parsedOn = this.parsedOn[key]
      // parse normal actions and bind `this`
      for (const listener in on) {
        if (on[listener] === 'submit') {
          // FIXME: nativeOn is not defined
          parsedOn[listener] = parsedOn[listener] || this.on.nativeOn.click.bind(this)
        } else if (typeof on[listener] === 'function') {
          parsedOn[listener] = parsedOn[listener] || on[listener].bind(this)
        }
      }
      // special judge for el-tree's onCheck
      if (type === 'tree') {
        const t = on.check
        parsedOn.check = (currentNode, checkedKeys) => {
          t && t.call(this, currentNode, checkedKeys)
          this.model[key] = checkedKeys
        }
      }
      // special judge for table's onCheck
      if (type === 'tableSelect') {
        const t = on.selectionChange
        parsedOn.selectionChange = (rows) => {
          t && t.call(this, rows)
          this.model[key] = rows
        }
      }
      return this.parsedOn[key]
    }
  },
  props: {
    form: Object,
    // TODO: anyway to avoid changing props directly?
    model: {
      type: Object,
      required: true
    },
    fields: Array,
    rules: Object,
    options: Object
  },
  data() {
    return {
      dependency: parseDependency(this.fields),
      parsedRules: parseRules(this),
      parsedOn: {}
    }
  },
  computed: {
    schemaHash() {
      return this.fields.reduce((prev, cur) => ({ ...prev, [cur.key]: cur }), {})
    },
    viewName() {
      return this.$route.params.view || 'list'
    }
  },
  created() {
    for (const field of this.fields) {
      if (typeof this.model[field.key] === 'undefined') {
        if (field.type === 'tree') {
          this.$set(this.model, field.key, [])
        } else {
          this.$set(this.model, field.key, field.options.defaultValue)
        }
      }
    }
  },
  watch: {
    // checked keys for el-tree is not responsive
    model(val) {
      this.fields && this.fields.forEach(field => {
        if (field.type === 'tree') {
          this.$refs[field.key].setCheckedKeys(val[field.key] || [])
        }
      })
    }
  },
  mounted() {
    this.form && Object.assign(this.form, this.$refs.form)
  }
}



// WEBPACK FOOTER //
// ./packages/components/FormTpl/index.js